import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, User } from 'lucide-react';
import type { FoundationData } from '../../types/Foundation';

interface Props {
  step: number;
  totalSteps: number;
  data: FoundationData;
  onImproveText: (text: string, field: string) => void;
}

export function AIAssistant({ 
  step, 
  totalSteps, 
  data,
  onImproveText 
}: Props) {
  const [messages, setMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([
    {
      role: 'assistant',
      content: 'Hej! Jag är din AI-assistent för att skapa stiftelsen. Hur kan jag hjälpa dig?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);

    setTimeout(() => {
      const response = generateResponse(userMessage);
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 1000);
  };

  const generateResponse = (message: string): string => {
    const responses = [
      "Jag förstår. Låt mig hjälpa dig med det. Baserat på din beskrivning föreslår jag...",
      "Det är en bra fråga. När det gäller stiftelser är det viktigt att...",
      "För att optimera din stiftelse skulle jag rekommendera att...",
      "Baserat på bästa praxis och juridiska krav bör du tänka på att..."
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  return (
    <div className="h-[600px] flex flex-col">
      <div className="flex-grow overflow-y-auto mb-4 space-y-4 p-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex items-start gap-3 ${
              message.role === 'user' ? 'justify-end' : ''
            }`}
          >
            {message.role === 'assistant' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
                <Bot className="w-5 h-5 text-rose-600" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-xl p-4 ${
                message.role === 'user'
                  ? 'bg-rose-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {message.content}
            </div>
            {message.role === 'user' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-600 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        ))}
        {isTyping && (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
              <Bot className="w-5 h-5 text-rose-600" />
            </div>
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2 p-4">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Skriv ditt meddelande här..."
          className="flex-grow rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 
                   transition-colors flex items-center gap-2"
        >
          <Send className="w-5 h-5" />
          <span>Skicka</span>
        </button>
      </form>
    </div>
  );
}